import { type BoardState } from '@shared/board'
import { readCard } from '@shared/card-markdown'
import { type Material, type MaterialText, materialHref } from '@shared/material'
import { matcher, readableLine } from '@shared/search'
import { type Editor, type Range } from '@tiptap/core'
import {
  BookOpen,
  FileText,
  Frame,
  Image,
  Link as LinkIcon,
  MessageSquare,
  Paperclip,
  Scissors,
} from 'lucide-react'
import type * as Y from 'yjs'
import { loadMaterialText } from '@/materials/hooks'
import { type SuggestItem } from './suggest'

// `@` in a card: the person's side of pointing (DESIGN.md "指涉"). It finds
// things the way ⌘K and the AI's `search` do (shared/search.ts) and writes
// the same links the AI writes, `item://` and `mat://`, drawn as chips.

const MAX = 8

export type MentionSource = {
  view: () => BoardState
  doc: Y.Doc
  materials: () => readonly Material[]
  /** Material text already loaded, by material id; asks for the rest. */
  text: (m: Material) => MaterialText | null
  /** The card being edited: it is not offered. */
  self: string
  t: (key: 'mention.untitled' | 'mention.page', vars?: Record<string, string | number>) => string
}

type Found = { item: SuggestItem; score: number; at: number }

/** Insert a link to `href` reading `label`, then a space to type on. */
function insertLink(label: string, href: string) {
  return (editor: Editor, range: Range) => {
    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        { type: 'text', text: label, marks: [{ type: 'link', attrs: { href } }] },
        { type: 'text', text: ' ' },
      ])
      .run()
  }
}

const short = (s: string, n = 60) => {
  const t = s.replace(/\s+/g, ' ').trim()
  return t.length > n ? `${t.slice(0, n - 1)}…` : t
}

export function mentionItems(src: MentionSource, query: string): SuggestItem[] {
  const q = query.trim()
  const score = matcher(q)
  const best = (text: string) => {
    let top = { score: 0, line: '' }
    for (const line of text.split('\n')) {
      const s = score(line)
      if (s > top.score) top = { score: s, line }
    }
    return top
  }
  const found: Found[] = []
  const s = src.view()
  for (const it of s.items.values()) {
    if (it.id === src.self) continue
    const href = `item://${it.id}`
    let title: string
    let body: string
    let icon = FileText
    if (it.kind === 'card') {
      const md = readCard(src.doc, it.id)
      title = readableLine(md.split('\n')[0] ?? '') || src.t('mention.untitled')
      body = md
    } else if (it.kind === 'message') {
      title = short(readableLine(it.data.text))
      body = it.data.text
      icon = MessageSquare
    } else if (it.kind === 'group') {
      title = it.data.title || src.t('mention.untitled')
      body = it.data.title
      icon = Frame
    } else if (it.kind === 'clip') {
      const m = src.materials().find((x) => x.id === it.data.materialId)
      title =
        short(it.data.text ?? '') ||
        src.t('mention.page', { name: m?.name ?? '', page: it.data.page })
      body = `${it.data.text ?? ''}\n${m?.name ?? ''}`
      icon = Scissors
    } else if (it.kind === 'image' || it.kind === 'file') {
      title = it.data.name
      body = it.data.name
      icon = it.kind === 'image' ? Image : Paperclip
    } else if (it.kind === 'link') {
      title = it.data.title || it.data.url
      body = [it.data.title, it.data.description, it.data.url].filter(Boolean).join('\n')
      icon = LinkIcon
    } else continue
    if (!q) {
      // Nothing typed yet: the latest cards and groups, the things people name.
      if (it.kind === 'card' || it.kind === 'group')
        found.push({
          item: { key: it.id, icon, label: title, run: insertLink(title, href) },
          score: 1,
          at: it.updatedAt,
        })
      continue
    }
    const b = best(body)
    const t = score(title)
    if (!b.score && !t) continue
    const line = readableLine(b.line)
    found.push({
      item: {
        key: it.id,
        icon,
        label: title,
        ...(line && line !== title && { detail: short(line, 80) }),
        run: insertLink(title, href),
      },
      // The name matching counts more than a line inside.
      score: Math.max(b.score, t * 2),
      at: it.updatedAt,
    })
  }
  for (const m of src.materials()) {
    const n = score(m.name)
    if (!q || n)
      found.push({
        item: {
          key: m.id,
          icon: BookOpen,
          label: m.name,
          run: insertLink(m.name, materialHref({ materialId: m.id, page: 1, rect: null })),
        },
        score: q ? n * 2 : 1,
        at: m.createdAt,
      })
    if (!q) continue
    src.text(m)?.pages.forEach((lines, i) => {
      for (const l of lines) {
        const sc = score(l.t)
        if (!sc) continue
        const label = src.t('mention.page', { name: m.name, page: i + 1 })
        const rect = { x: l.x, y: l.y, w: l.w, h: l.h }
        found.push({
          item: {
            key: `${m.id}:${i + 1}:${l.y}`,
            icon: BookOpen,
            label,
            detail: short(l.t, 80),
            run: insertLink(label, materialHref({ materialId: m.id, page: i + 1, rect })),
          },
          score: sc,
          at: 0,
        })
      }
    })
  }
  return found
    .sort((a, b) => b.score - a.score || b.at - a.at)
    .slice(0, MAX)
    .map((f) => f.item)
}

/** `@` opens after a space, at the start of a line, or after CJK text; never inside an address. */
export function mentionAllowed(before: string): boolean {
  const c = before.at(-1)
  return c === undefined || !/[\w.%+-]/.test(c)
}

const loadedTexts = new Map<string, MaterialText | null>()

/** A material's text for `@`, once loaded: the first ask fetches it, later ones find it. */
export function materialText(boardId: string, m: Material): MaterialText | null {
  if (!loadedTexts.has(m.id)) {
    loadedTexts.set(m.id, null)
    void loadMaterialText(boardId, m).then((t) => loadedTexts.set(m.id, t))
  }
  return loadedTexts.get(m.id) ?? null
}
